/**
 * The only module (besides amplify-configure.ts) that talks to AWS Amplify.
 * Everything else in the app goes through these functions, which keeps the SDK
 * swappable and gives the rest of the code base typed token claims.
 */
import {
  fetchAuthSession,
  signInWithRedirect,
  signOut as amplifySignOut,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

import { NoSessionError } from '$shared/auth/no-session-error';

export interface IdTokenClaims {
  sub: string;
  email: string;
  tenantId: string;
  permissions: string;
  profile?: string;
  [claim: string]: unknown;
}

export type AuthEvent =
  | { type: 'signedIn' }
  | { type: 'customState'; state: string }
  | { type: 'refreshFailed' };

export { NoSessionError };

/**
 * Amplify v6's `fetchAuthSession` resolves with `tokens: undefined` instead of rejecting
 * when nobody is signed in. Callers rely on a rejection (redirect to login, route
 * loaders, ...), so we turn the missing session into a `NoSessionError`.
 * Expired tokens are refreshed automatically by Amplify.
 */
async function getIdToken() {
  const { tokens } = await fetchAuthSession();
  if (!tokens?.idToken) {
    throw new NoSessionError();
  }
  return tokens.idToken;
}

export async function getIdTokenString(): Promise<string> {
  return (await getIdToken()).toString();
}

export async function getIdTokenPayload(): Promise<IdTokenClaims> {
  return (await getIdToken()).payload as unknown as IdTokenClaims;
}

export async function hasSession(): Promise<boolean> {
  try {
    await getIdToken();
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Redirects to the Cognito Hosted UI. `customState` is passed as the OAuth `state` param
 * and handed back via the `customState` auth event once the redirect completed.
 */
export async function signInWithHostedUi(customState?: string): Promise<void> {
  // no `provider` => Cognito Hosted UI (managed login)
  await signInWithRedirect({ customState });
}

export async function signOut(): Promise<void> {
  await amplifySignOut();
}

/** Subscribes to auth events. Returns the unsubscribe function. */
export function onAuthEvent(handler: (event: AuthEvent) => void): () => void {
  return Hub.listen('auth', ({ payload }) => {
    switch (payload.event) {
      case 'signedIn':
        handler({ type: 'signedIn' });
        break;
      case 'customOAuthState':
        handler({ type: 'customState', state: payload.data });
        break;
      case 'tokenRefresh_failure':
        handler({ type: 'refreshFailed' });
        break;
      default:
        break;
    }
  });
}
